import { useState } from 'react';
import { api } from '../api';
import { useLoad } from '../hooks';
import { stamp, timeAgo } from '../format';
import {
  Card,
  ConfirmButton,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Modal,
  Table,
  Toggle,
} from '../components/ui';
import type { ApiKey, CreatedApiKey } from '../types';

/**
 * Makine kimlik bilgileri. Açık metin sır tam olarak tek bir yanıtta, oluşturma anında
 * var; geri okunabileceği hiçbir yerde saklanmıyor — o yüzden arayüz bir kez gösterir
 * ve bunu açıkça söyler.
 */
export function ApiKeys() {
  const keys = useLoad(() => api.get<ApiKey[]>('/v1/auth/api-keys'));
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<CreatedApiKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  const revoke = (id: string) => {
    setError(null);
    api
      .del(`/v1/auth/api-keys/${id}`)
      .then(() => keys.reload())
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      );
  };

  const rows = keys.data ?? [];

  return (
    <>
      <Card
        title={`API anahtarları${rows.length ? ` (${rows.length})` : ''}`}
        flush
        actions={
          <button type="button" onClick={() => setCreating(true)}>
            Yeni anahtar
          </button>
        }
        footer="API anahtarları yalnızca actor, çalışma ve veri kümesi uçlarına erişir. Kimlik, proxy ve havuz yönetimi konsol kullanıcısına özeldir."
      >
        {(error ?? keys.error) && (
          <div style={{ padding: '14px 16px 0' }}>
            <ErrorNote message={error ?? keys.error} />
          </div>
        )}
        {keys.loading && rows.length === 0 && <Loading />}
        {!keys.loading && rows.length === 0 && (
          <Empty title="API anahtarı yok">
            NEREYE gibi bir tüketici bağlamak için buradan anahtar üret.
          </Empty>
        )}
        {rows.length > 0 && (
          <Table head={['Ad', 'Ön ek', 'Son kullanım', 'Geçerlilik', 'Oluşturma', '']}>
            {rows.map((key) => (
              <tr key={key.id} className={key.revokedAt ? 'row-retired' : undefined}>
                <td className="cell-title">
                  {key.name}
                  {key.revokedAt && <span className="muted"> · iptal edildi</span>}
                </td>
                <td className="mono muted">{key.prefix}…</td>
                <td className="muted" title={stamp(key.lastUsedAt)}>
                  {timeAgo(key.lastUsedAt)}
                </td>
                <td className="muted small">{key.expiresAt ? stamp(key.expiresAt) : 'süresiz'}</td>
                <td className="muted small">{stamp(key.createdAt)}</td>
                <td className="actions">
                  {!key.revokedAt && (
                    <ConfirmButton
                      label="İptal et"
                      confirmLabel="İptal edilsin mi?"
                      onConfirm={() => revoke(key.id)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {creating && (
        <CreateKey
          onClose={() => setCreating(false)}
          onDone={(created) => {
            setCreating(false);
            setRevealed(created);
            keys.reload();
          }}
        />
      )}

      {revealed && (
        <Modal
          title="Bu anahtarı şimdi kopyala"
          submitLabel="Kopyaladım"
          onClose={() => setRevealed(null)}
          onSubmit={() => setRevealed(null)}
        >
          <p className="note">
            Sır yalnızca bu bir kez gösterilir. Yalnızca özeti saklandığı için geri getirilemez —
            kaybedersen yeni anahtar üretmen gerekir.
          </p>
          <pre className="code selectable">{revealed.secret}</pre>
        </Modal>
      )}
    </>
  );
}

function CreateKey({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (key: CreatedApiKey) => void;
}) {
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [opsRead, setOpsRead] = useState(false);
  const [resourcesWrite, setResourcesWrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scopes = [
    ...(opsRead ? ['operations:read'] : []),
    ...(resourcesWrite ? ['resources:write'] : []),
  ];

  const submit = () => {
    setBusy(true);
    setError(null);
    api
      .post<CreatedApiKey>('/v1/auth/api-keys', {
        name,
        ...(scopes.length ? { scopes } : {}),
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      })
      .then(onDone)
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title="Yeni API anahtarı"
      submitLabel="Oluştur"
      onClose={onClose}
      onSubmit={submit}
      busy={busy}
      error={error}
    >
      <Field label="Ad" hint="Bu anahtarı ne kullanıyor. Denetim kaydında görünür.">
        <input value={name} onChange={(e) => setName(e.target.value)} required />
      </Field>
      <Field label="Bitiş tarihi" hint="İsteğe bağlı. Boş bırakılırsa süresiz.">
        <input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
      </Field>
      {/* `Field` BİLEREK kullanılmıyor: o bir <label> ve içine ikinci bir etiketli denetim
          koymak geçersiz HTML — iç içe label'da tıklamanın hangi denetimi hedeflediği
          tarayıcıya kalıyor. Kutu görünmüyordu, sebebi buydu (bir de hiç eklenmemiş bir
          stil kuralı). Aynı görünüm, panelin başka yerlerinde zaten kullanılan anahtarla.

          Varsayılan KAPALI ve öyle kalmalı: izleme uçları harcamayı ve hata metinlerini
          taşıyor. Veri okumak için verilmiş bir anahtara bunu açmak, o anahtar sızarsa
          workspace'in ne harcadığının da sızması demek. */}
      <div className="field">
        <span className="field-label">İzleme erişimi</span>
        <div className="switch-row">
          <Toggle on={opsRead} label="İzleme erişimi" onChange={setOpsRead} />
          <code>operations:read</code>
        </div>
        <span className="field-hint">
          Açılırsa bu anahtar harcamayı, hata metinlerini ve sistem durumunu okuyabilir.
          Sıradan bir anahtar için gerekmez.
        </span>
      </div>
      {/* Diğerlerinden farklı: bu bir YAZMA izni. Kaynak uçları makine anahtarlarını
          baştan dışarıda tutuyordu, çünkü kimlik ve proxy eklemek bir operatör eylemi.
          Açık bir ihtiyaç olmadıkça kapalı kalmalı. */}
      <div className="field">
        <span className="field-label">Kaynak yönetimi</span>
        <div className="switch-row">
          <Toggle on={resourcesWrite} label="Kaynak yönetimi" onChange={setResourcesWrite} />
          <code>resources:write</code>
        </div>
        <span className="field-hint">
          Açılırsa bu anahtar hesap ve proxy ekleyip düzenleyebilir, silebilir ve test
          edebilir. Saklanan şifre veya oturumu geri okuyamaz; tarayıcıdan giriş ve
          doğrulama kodu adımları konsola özel kalır. Anahtar sızarsa havuzun tamamı
          yönetilebilir hale gelir — yalnızca gerçekten gerekiyorsa aç.
        </span>
      </div>
    </Modal>
  );
}
